<template>
  <BaseModal
    :id="`${$options.name}-${instanceId}`"
    data-module="warning-popup"
    class="warning-popup"
    :show-on-desk="showOnDesk"
    :show-on-mobile="showOnMobile"
    :style="{
      zIndex,
      '--primary-color': primaryColor,
      '--background-color': backgroundColor,
      '--text-color': textColor,
      '--cta-color': ctaColor,
      '--button-text-color': buttonTextColor,
      '--title-weight': titleWeight,
      '--bottom': bottom + 'px',
    }"
  >
    <div class="warning-popup__content">
      <div class="warning-popup__content__wrapper">
        <IconClose
          color="#888888"
          title="Fechar"
          size="xs"
        />
      </div>
      <div class="warning-popup__container">
        <h4 v-if="title">
          {{ title }}
        </h4>
        <div
          v-if="text"
          class="warning-popup__container__text"
          v-html="text"
        ></div>
        <EditorLink
          v-if="linkLabel"
          class="cta"
          :model="linkUrl"
        >
          {{ linkLabel }}
        </EditorLink>
      </div>
    </div>
  </BaseModal>
</template>

<script>
import IconClose from '@/components/icons/IconClose.vue';
import weightOptions from '@/helpers/weightOptions.json';
import BaseModal from '../generic/modals/BaseModal.vue';
import EditorLink from '../EditorLink.vue';

export default {
  name: 'WarningPopup',
  components: {
    EditorLink,
    BaseModal,
    IconClose,
  },
  inheritAttrs: false,
  props: {
    ...BaseModal.props,
    instanceId: {
      type: String,
      default: '',
    },
    bottom: {
      type: Number,
      default: 170,
    },
    backgroundColor: {
      type: String,
      default: '#FCFCFC',
    },
    primaryColor: {
      type: String,
      default: '#3E9AA6',
    },
    ctaColor: {
      type: String,
      default: '#F27335',
    },
    textColor: {
      type: String,
      default: '',
    },
    buttonTextColor: {
      type: String,
      default: '#FFFFFF',
    },
    title: {
      type: String,
      default: '',
    },
    titleWeight: {
      type: String,
      default: '600',
    },
    text: {
      type: String,
      default: '',
    },
    linkLabel: {
      type: String,
      default: '',
    },
    linkUrl: {
      type: Object,
      default: () => ({
        type: 'externalLink',
        value: '',
        openInNewTab: true,
      }),
    },
  },
  metadata: {
    extends: BaseModal.metadata,
    tabs: [
      {
        name: 'content',
        groups: [
          {
            name: 'text',
            label: 'Contéudo',
            fields: [
              {
                name: 'title',
                type: 'ShortText',
                label: 'Título',
              },
              {
                name: 'titleWeight',
                type: 'VueSelect',
                label: 'Peso do título',
                default: '300',
                options: weightOptions,
                isEnabled({ title }) {
                  return title;
                },
              },
              {
                name: 'text',
                label: 'Texto do pop-up',
                type: 'Html',
                maxlength: 300,
              },
              {
                name: 'linkLabel',
                label: 'Texto do botão',
                type: 'ShortText',
                maxlength: 35,
              },
              {
                name: 'linkUrl',
                label: 'URL do link',
                type: 'Url',
              },
            ],
          },
        ],
      },
      {
        name: 'appearance',
        label: 'Aparência',
        groups: [
          {
            name: 'colors',
            label: 'Cores',
            fields: [
              {
                name: 'backgroundColor',
                type: 'ColorPicker',
                label: 'Cor de fundo',
              },
              {
                name: 'textColor',
                type: 'ColorPicker',
                label: 'Cor do texto',
              },
              {
                name: 'ctaColor',
                type: 'ColorPicker',
                label: 'Cor do botão',
              },
              {
                name: 'buttonTextColor',
                type: 'ColorPicker',
                label: 'Cor de texto do botão',
              },
            ],
          },
          {
            name: 'position',
            label: 'Posicionamento',
            fields: [
              {
                name: 'bottom',
                label: 'Posição vertical',
                helpText:
                  'Contagem a partir da borda inferior da janela do navegador.',
                type: 'Range',
                min: 0,
                max: 200,
                step: 1,
                format: {
                  suffix: 'px',
                },
              },
            ],
          },
        ],
      },
    ],
  },
};
</script>

<style scoped lang="scss">
.warning-popup {
  position: absolute;
  bottom: var(--bottom);
  left: 20px;
  max-width: 400px;

  @media (max-width: 440px) {
    left: 0;
    max-width: 100%;
  }

  &__content {
    display: block;
    border-radius: 12px;
    background-color: var(--background-color, $lb-ice);
    padding: 25px;

    &__wrapper {
      > i {
        position: absolute;
        top: 15px;
        right: 15px;
        cursor: pointer;
        z-index: 1;
      }
    }
  }

  &__container {
    margin: 10px 10px 0 0;

    > h4 {
      color: var(--text-color);
      font-weight: var(--title-weight, 600);

      @media (max-width: 440px) {
        font-size: $size-small;
      }
    }
  }

  &__container__text {
    color: var(--text-color);
    opacity: 0.6;

    &::v-deep {
      p {
        line-height: 1.2;

        @media (max-width: 440px) {
          font-size: $size-smaller;
        }
      }
    }
  }
}

.cta {
  min-width: 97px;
  text-align: center;
  display: inline-block;
  padding: 7px 20px;
  border-radius: 10px;
  min-height: unset;
  text-transform: unset;
  font-weight: 500;
  font-size: $size-small;
  background-color: var(--cta-color);
  color: var(--button-text-color);
  margin-top: 20px;
  text-transform: uppercase;
  overflow: hidden;

  @include media-breakpoint-down(sm) {
    margin-bottom: 15px;
  }
}
</style>
